import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, concat, from, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap } from 'rxjs/operators';
import { AuthenticateRequestModel, AuthenticateResponseModel, RefreshTokenModel} from 'src/app/models/authorize/authenticate-model';
import { IUserModel, UserModel } from 'src/app/models/authorize/user-model';
import { ApplicationPaths } from '../../constants/authorization/api-authorization.constants';
import { Router } from '@angular/router';
import { UserClientSettingName, UserClientSettingsItem } from '../../models/config/config-item-model';

@Injectable({
  providedIn: 'root'
})
export class AuthorizeService {

    private POST_USER_LOGIN_URL = '/api/user/authenticate';
    private POST_REFRESH_TOKEN_URL = '/api/user/refresh-token';
    private POST_REVOKE_TOKEN_URL = '/api/user/revoke-token';

    private _userBehaviorSubject: BehaviorSubject<IUserModel | null> = new BehaviorSubject<IUserModel | null>(null);

    private _triggerRefreshTokenSubject: Subject<boolean> = new Subject<boolean>();
    private _triggerRefreshTokenObservable: Observable<boolean> = this._triggerRefreshTokenSubject.asObservable();
    private _refreshNowTicks: number = 0;

    // Timer to check for idle.
    private _deadmanTimer: any | null = null;

    // Client configuration
    private _userClientSettings: UserClientSettingsItem[] = [];

    constructor(
        private _httpClient: HttpClient,
        private _router: Router
    ) {

        this._triggerRefreshTokenObservable.subscribe(tick => { this._triggerRefresh(); })
        this.user$.subscribe(user => { this._userChanged(user); });

        // Create default client settings.
        //this._userClientSettings.push(new UserClientSettingsItem({ name: UserClientSettingName.AutoLogoutMinutes, value: '30' }));
        //this._userClientSettings.push(new UserClientSettingsItem({ name: UserClientSettingName.RefreshTokenPeriodMinutes, value: '10' }));
        this._userClientSettings.push(new UserClientSettingsItem({ name: UserClientSettingName.AutoLogoutMinutes, value: '120' }));  //TEMPORARY REMOVE - PUT THIS BACK TO 60 AFTER TRADESHOW
        this._userClientSettings.push(new UserClientSettingsItem({ name: UserClientSettingName.RefreshTokenPeriodMinutes, value: '120' })); //TEMPORARY REMOVE - PUT THIS BACK TO 60 AFTER TRADESHOW
    }

    set triggerRefreshToken(tick: boolean) {
        this._triggerRefreshTokenSubject.next(tick);
    }

    get isAuthenticated$(): Observable<boolean> {
        return this.user$.pipe(map(u => !!u));
    }

    get user$(): Observable<IUserModel | null> {
        return concat(
            this._userBehaviorSubject.pipe(take(1), filter(u => !!u)),
            this._userBehaviorSubject.asObservable());
    }

    get accessToken$(): Observable<string | null> {
        return of(this._userBehaviorSubject.value?.jwtToken ?? null);
    }

    authenticate$(authenticateRequestModel: AuthenticateRequestModel): Observable<AuthenticateResponseModel | null> {
        return this._httpClient.post<AuthenticateResponseModel>(this.POST_USER_LOGIN_URL, authenticateRequestModel).pipe(
            tap(response => {
                if (!!response && response.success) {
                    this._refreshClientSettings(response);
                    this._refreshNowTicks = Date.now() + this.getClientSetting(UserClientSettingName.RefreshTokenPeriodMinutes) * 60 * 1000; //Converts minutes to milliseconds
                    this._userBehaviorSubject.next(response);
                }
            }),
            catchError(error => {
                return of(null);
            }),      
        );
    }

    signOut() {
        this._revokeToken();
        this._userBehaviorSubject.next(null);
    }

    checkAuthorization(required: string, rights: string | undefined): boolean {
        if (rights !== undefined) {
            // Step 1: Split the input strings into arrays of numbers
            const arrRequired = required.split(',').map(Number);
            const arrRights = rights.split(',').map(Number);

            // Step 2: Use array functions to check for common numbers
            const commonNumbers = arrRequired.filter((num) => arrRights.includes(num));
            if (commonNumbers.length > 0) {
                return true;
            }
            else {
                return false;
            }
        }
        else {
            return false;
        }
    }

    private _triggerRefresh() {

        // Protect against processing if user is logged out.
        if (!!this._userBehaviorSubject && !!this._userBehaviorSubject.value && !!this._userBehaviorSubject.value.jwtToken) {

            this._resetDeadManTimer();

            if (this._refreshNowTicks > 0 && this._refreshNowTicks < Date.now()) {
                //DBW We are refresshing the token
                this._refreshNowTicks = Date.now() + this.getClientSetting(UserClientSettingName.RefreshTokenPeriodMinutes) * 60 * 1000; //Converts minutes to milliseconds
                this._refreshToken$().subscribe(response => {
                    if (!!response && response.success) {
                        this._refreshClientSettings(response);
                        this._userBehaviorSubject.next(response);
                    }
                    else {
                        // Force a logout.
                        //this._router.navigateByUrl(ApplicationPaths.LogOut); //TEMPORARY REMOVE - PUT THIS BACK AFTER TRADESHOW
                    }
                }, error => {
                    // Force a logout.
                    this._router.navigateByUrl(ApplicationPaths.LogOut);
                });
            }
        }
    }

    private _refreshToken$(): Observable<AuthenticateResponseModel | null> {
        //DBW Async call triggered by above subscribe in _triggerRefresh

        return !this._userBehaviorSubject || !this._userBehaviorSubject.value ?
            of(null)
            :
            this._httpClient.post<AuthenticateResponseModel | null>(this.POST_REFRESH_TOKEN_URL, new RefreshTokenModel(this._userBehaviorSubject.value.refreshToken));
    }

    private _revokeToken() {

        if (!!this._userBehaviorSubject && !!this._userBehaviorSubject.value && !!this._userBehaviorSubject.value.refreshToken) {
            this._httpClient.post<AuthenticateResponseModel | null>(this.POST_REVOKE_TOKEN_URL, new RefreshTokenModel(this._userBehaviorSubject.value.refreshToken))
                .subscribe();
        }
    }


    private _resetDeadManTimer() {

        if (!!this._deadmanTimer) {
            clearTimeout(this._deadmanTimer);
            this._deadmanTimer = null;
        }

        this._deadmanTimer = setTimeout(() => {
            // Force a logout.
            //this._router.navigateByUrl(ApplicationPaths.LogOut);  NOTE: Commenting out temporarily
        }, this.getClientSetting(UserClientSettingName.AutoLogoutMinutes) * 60 * 1000);
    }

    private _userChanged(user: IUserModel | null) {

        if (user === null) {
            if (!!this._deadmanTimer) {
                clearTimeout(this._deadmanTimer);
                this._deadmanTimer = null;
            }
        }
        else {
            this._resetDeadManTimer();
        }
    }


    getClientSetting(settingName: UserClientSettingName): number {
        const entry: UserClientSettingsItem | undefined = this._userClientSettings.find(existingSetting => existingSetting.name === settingName);
        return !!entry ? +entry.value : 0;
    }


    private _refreshClientSettings(authenticateResponseModel: AuthenticateResponseModel) {
        if (!!authenticateResponseModel && !!authenticateResponseModel.userClientSettings) {
            const newClientSettings: UserClientSettingsItem[] = [];
            authenticateResponseModel.userClientSettings.forEach(newSetting => {
                const entry: UserClientSettingsItem | undefined = this._userClientSettings.find(existingSetting => existingSetting.name === newSetting.name)
                if (!!entry) {
                    entry.value = newSetting.value;
                }
                else {
                    newClientSettings.push(new UserClientSettingsItem(newSetting));
                }
            });

            this._userClientSettings.concat(newClientSettings);
        }
    }

}
